<template>
  <div class="quick-view-container" v-if="product">
    <!-- 商品图片区域 -->
    <div class="image-section">
      <!-- 主图 -->
      <div class="main-image-container">
        <el-image
          :src="currentImage"
          :alt="product.name"
          fit="cover"
          class="main-image"
          :preview-src-list="product.images || [product.image]"
          hide-on-click-modal
        >
          <template #placeholder>
            <div class="image-loading">
              <el-icon class="loading-icon"><Loading /></el-icon>
              <span>图片加载中...</span>
            </div>
          </template>
          <template #error>
            <div class="image-error">
              <el-icon><Picture /></el-icon>
              <span>图片加载失败</span>
            </div>
          </template>
        </el-image>
        
        <!-- 商品标签 -->
        <div class="product-tags">
          <el-tag 
            v-if="product.discount" 
            type="danger" 
            size="small"
            class="discount-tag"
          >
            -{{ product.discount }}% OFF
          </el-tag>
          <el-tag 
            v-if="product.isNew" 
            type="success" 
            size="small"
            class="new-tag"
          >
            新品
          </el-tag>
          <el-tag 
            v-if="product.isHot" 
            type="warning" 
            size="small"
            class="hot-tag"
          >
            热销
          </el-tag>
          <el-tag 
            v-if="product.stock <= 10 && product.stock > 0" 
            type="warning" 
            size="small"
            class="stock-tag"
          >
            仅剩{{ product.stock }}件
          </el-tag>
          <el-tag 
            v-if="product.stock <= 0" 
            type="info" 
            size="small"
            class="out-of-stock-tag"
          >
            已售罄
          </el-tag>
        </div>
        
        <!-- 图片缩略图 -->
        <div class="thumbnail-list" v-if="product.images && product.images.length > 1">
          <div
            v-for="(img, index) in product.images"
            :key="index"
            class="thumbnail-item"
            :class="{ active: currentImageIndex === index }"
            @click="changeImage(index)"
          >
            <el-image
              :src="img"
              fit="cover"
              class="thumbnail-image"
            >
              <template #error>
                <div class="thumbnail-error">
                  <el-icon><Picture /></el-icon>
                </div>
              </template>
            </el-image>
          </div>
        </div>
      </div>
    </div>

    <!-- 商品信息区域 -->
    <div class="info-section">
      <!-- 商品标题 -->
      <div class="product-header">
        <h2 class="product-title">{{ product.name }}</h2>
        <div class="product-code">
          <span class="code-label">货号：</span>
          <span class="code-value">{{ product.code }}</span>
          <el-button
            type="text"
            size="small"
            @click="copyProductCode"
            class="copy-btn"
          >
            <el-icon><DocumentCopy /></el-icon>
            复制
          </el-button>
        </div>
      </div>

      <!-- 价格信息 -->
      <div class="price-section">
        <div class="price-row">
          <div class="current-price">
            <span class="price-symbol">¥</span>
            <span class="price-number">{{ formatPrice(product.price) }}</span>
            <span v-if="product.discount" class="discount-percent">
              -{{ product.discount }}%
            </span>
          </div>
          <div v-if="product.originalPrice" class="original-price">
            原价：
            <span class="price-strikethrough">¥{{ formatPrice(product.originalPrice) }}</span>
          </div>
        </div>
        <div class="price-tips">
          <el-icon><InfoFilled /></el-icon>
          <span>此价格为不含税价格</span>
        </div>
      </div>

      <!-- 评分信息 -->
      <div class="rating-section">
        <div class="rating-content">
          <el-rate
            v-model="product.rating"
            disabled
            show-score
            text-color="#ff9900"
            score-template="{value}"
            class="rating-stars"
          />
          <span class="review-count">{{ product.reviewCount || 0 }}条评价</span>
          <span class="sales-count">已售{{ product.sales || 0 }}</span>
        </div>
        <el-link type="primary" :underline="false" @click="scrollToReviews">
          查看评价详情
        </el-link>
      </div>

      <!-- 规格选择 -->
      <div class="spec-section" v-if="product.specifications && product.specifications.length > 0">
        <h3 class="section-title">规格选择</h3>
        <div class="spec-list">
          <el-radio-group 
            v-model="selectedSpecId" 
            @change="handleSpecChange"
            class="spec-group"
          >
            <el-radio
              v-for="spec in product.specifications"
              :key="spec.id"
              :label="spec.id"
              :disabled="spec.stock <= 0"
              class="spec-radio"
            >
              <div class="spec-content">
                <span class="spec-name">{{ spec.name }}</span>
                <span class="spec-price">¥{{ formatPrice(spec.price) }}</span>
                <el-tag 
                  v-if="spec.stock <= 0" 
                  type="info" 
                  size="mini"
                  class="stock-tag"
                >
                  已售罄
                </el-tag>
                <span v-else class="stock-info">库存 {{ spec.stock }}</span>
              </div>
            </el-radio>
          </el-radio-group>
        </div>
      </div>

      <!-- 数量选择 -->
      <div class="quantity-section">
        <h3 class="section-title">数量</h3>
        <div class="quantity-control">
          <el-input-number
            v-model="quantity"
            :min="1"
            :max="maxQuantity"
            controls-position="right"
            @change="handleQuantityChange"
            class="quantity-input"
          />
          <div class="stock-info">
            <span v-if="currentStock > 0">
              库存 {{ currentStock }} 件
            </span>
            <span v-else class="out-of-stock-text">已售罄</span>
          </div>
        </div>
      </div>

      <!-- 配送信息 -->
      <div class="shipping-section">
        <h3 class="section-title">配送信息</h3>
        <div class="shipping-content">
          <div class="shipping-item">
            <el-icon><Location /></el-icon>
            <span>送至：</span>
            <el-select
              v-model="selectedAddress"
              placeholder="请选择收货地址"
              size="small"
              class="address-select"
            >
              <el-option label="北京市朝阳区" value="beijing" />
              <el-option label="上海市浦东新区" value="shanghai" />
              <el-option label="广州市天河区" value="guangzhou" />
            </el-select>
          </div>
          <div class="shipping-item">
            <el-icon><Truck /></el-icon>
            <span>预计1-3个工作日送达</span>
            <el-link type="primary" :underline="false" @click="showShippingDetail">
              查看详情
            </el-link>
          </div>
          <div class="shipping-item">
            <el-icon><Box /></el-icon>
            <span>支持货到付款</span>
          </div>
        </div>
      </div>

      <!-- 服务承诺 -->
      <div class="service-section">
        <div class="service-list">
          <div class="service-item">
            <el-icon><Medal /></el-icon>
            <span>正品保证</span>
          </div>
          <div class="service-item">
            <el-icon><Check /></el-icon>
            <span>专业支持</span>
          </div>
          <div class="service-item">
            <el-icon><Clock /></el-icon>
            <span>快速发货</span>
          </div>
          <div class="service-item">
            <el-icon><Service /></el-icon>
            <span>无忧售后</span>
          </div>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="action-section">
        <div class="action-buttons">
          <el-button
            type="primary"
            size="large"
            :loading="addingToCart"
            :disabled="!canAddToCart"
            @click="handleAddToCart"
            class="add-cart-btn"
          >
            <el-icon><ShoppingCart /></el-icon>
            {{ buttonText }}
          </el-button>
          
          <el-button
            type="danger"
            size="large"
            :loading="buyingNow"
            :disabled="!canAddToCart"
            @click="handleBuyNow"
            class="buy-now-btn"
          >
            <el-icon><ShoppingBag /></el-icon>
            立即购买
          </el-button>
          
          <el-button
            type="default"
            size="large"
            :plain="!isInWishlist"
            @click="handleAddToWishlist"
            class="wishlist-btn"
          >
            <el-icon><Star /></el-icon>
            {{ isInWishlist ? '已收藏' : '加入收藏' }}
          </el-button>
        </div>
        
        <div class="quick-actions">
          <el-button type="text" @click="handleViewDetail" class="detail-btn">
            <el-icon><View /></el-icon>
            查看商品详情
          </el-button>
          <el-button type="text" @click="handleShare" class="share-btn">
            <el-icon><Share /></el-icon>
            分享商品
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
// import { useClipboard } from '@vueuse/core'
// import { useCartStore } from '@/stores/cart'
// import { useWishlistStore } from '@/stores/wishlist'
// import type { Product } from '@/types/product'

// 图标导入
import {
  Loading,
  Picture,
  DocumentCopy,
  InfoFilled,
  Location,
  // Truck,
  Box,
  Medal,
  Check,
  Clock,
  Service,
  ShoppingCart,
  ShoppingBag,
  Star,
  View,
  Share
} from '@element-plus/icons-vue'

interface Props {
  product: Product
}

interface Emits {
  (e: 'add-to-cart', product: Product): void
  (e: 'buy-now', product: Product): void
  (e: 'close'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const router = useRouter()
const cartStore = useCartStore()
const wishlistStore = useWishlistStore()
const { copy } = useClipboard()

// 响应式数据
const currentImageIndex = ref(0)
const selectedSpecId = ref<number | null>(null)
const quantity = ref(1)
const selectedAddress = ref('beijing')
const addingToCart = ref(false)
const buyingNow = ref(false)

// 计算属性
const currentImage = computed(() => {
  if (!props.product || !props.product.images) return ''
  return props.product.images[currentImageIndex.value] || props.product.image
})

const currentSpec = computed(() => {
  if (!selectedSpecId.value || !props.product.specifications) return null
  return props.product.specifications.find(spec => spec.id === selectedSpecId.value)
})

const currentStock = computed(() => {
  if (currentSpec.value) {
    return currentSpec.value.stock
  }
  return props.product.stock || 0
})

const maxQuantity = computed(() => {
  return Math.min(currentStock.value, 999)
})

const canAddToCart = computed(() => {
  // 如果商品有规格，必须选择规格才能加入购物车
  if (props.product.specifications && props.product.specifications.length > 0) {
    return selectedSpecId.value !== null && currentStock.value > 0
  }
  // 如果没有规格，直接判断库存
  return currentStock.value > 0
})

const buttonText = computed(() => {
  if (!canAddToCart.value) {
    return '已售罄'
  }
  return '加入购物车'
})

const isInWishlist = computed(() => {
  if (!props.product) return false
  return wishlistStore.isInWishlist(props.product.id)
})

// 方法
const changeImage = (index: number) => {
  currentImageIndex.value = index
}

const formatPrice = (price: number): string => {
  return price.toLocaleString('zh-CN')
}

const copyProductCode = async () => {
  try {
    await copy(props.product.code)
    ElMessage.success('货号已复制到剪贴板')
  } catch (error) {
    ElMessage.error('复制失败')
  }
}

const handleSpecChange = () => {
  // 切换规格时重置数量为1
  quantity.value = 1
}

const handleQuantityChange = (value: number) => {
  if (value > maxQuantity.value) {
    quantity.value = maxQuantity.value
    ElMessage.warning(`最多只能购买${maxQuantity.value}件`)
  }
}

const handleAddToCart = async () => {
  if (!canAddToCart.value) return
  
  addingToCart.value = true
  try {
    // 模拟API调用延迟
    await new Promise(resolve => setTimeout(resolve, 500))
    
    const price = currentSpec.value?.price || props.product.price
    
    cartStore.addItem({
      productId: props.product.id,
      product: props.product,
      specId: selectedSpecId.value || undefined,
      quantity: quantity.value,
      price
    })
    
    ElMessage.success(`已添加 ${props.product.name} 到购物车`)
    emit('add-to-cart', props.product)
    
    // 添加成功后自动关闭弹窗
    setTimeout(() => {
      emit('close')
    }, 1000)
  } catch (error) {
    console.error('添加到购物车失败:', error)
    ElMessage.error('添加到购物车失败')
  } finally {
    addingToCart.value = false
  }
}

const handleBuyNow = async () => {
  if (!canAddToCart.value) return
  
  buyingNow.value = true
  try {
    await handleAddToCart()
    emit('buy-now', props.product)
    router.push('/checkout')
  } finally {
    buyingNow.value = false
  }
}

const handleAddToWishlist = () => {
  if (isInWishlist.value) {
    wishlistStore.removeItem(props.product.id)
    ElMessage.success('已从收藏移除')
  } else {
    wishlistStore.addItem(props.product)
    ElMessage.success('已加入收藏')
  }
}

const handleViewDetail = () => {
  emit('close')
  router.push(`/product/${props.product.id}`)
}

const handleShare = () => {
  const shareUrl = `${window.location.origin}/product/${props.product.id}`
  copy(shareUrl).then(() => {
    ElMessage.success('商品链接已复制到剪贴板')
  })
}

const scrollToReviews = () => {
  // 在实际项目中，这里会滚动到评价部分
  ElMessage.info('查看评价功能')
}

const showShippingDetail = () => {
  ElMessage.info('查看配送详情')
}

// 初始化
onMounted(() => {
  // 如果有规格，默认选择第一个有库存的规格
  if (props.product.specifications && props.product.specifications.length > 0) {
    const availableSpec = props.product.specifications.find(spec => spec.stock > 0)
    if (availableSpec) {
      selectedSpecId.value = availableSpec.id
    } else {
      // 如果所有规格都售罄，选择第一个规格
      selectedSpecId.value = props.product.specifications[0].id
    }
  }
})

// 监听商品变化
watch(
  () => props.product,
  () => {
    currentImageIndex.value = 0
    quantity.value = 1
    
    // 重置规格选择
    if (props.product.specifications && props.product.specifications.length > 0) {
      const availableSpec = props.product.specifications.find(spec => spec.stock > 0)
      selectedSpecId.value = availableSpec ? availableSpec.id : props.product.specifications[0].id
    } else {
      selectedSpecId.value = null
    }
  },
  { immediate: true }
)
</script>

<style scoped lang="scss">
.quick-view-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  max-height: 80vh;
  overflow-y: auto;
  padding-right: 8px;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
  
  /* 滚动条样式 */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--el-fill-color-light);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--el-text-color-disabled);
    border-radius: 3px;
    
    &:hover {
      background: var(--el-text-color-secondary);
    }
  }
}

/* 图片区域 */
.image-section {
  .main-image-container {
    position: relative;
    
    .main-image {
      width: 100%;
      height: 400px;
      border-radius: 12px;
      overflow: hidden;
      background: var(--el-fill-color-lighter);
      
      :deep(.el-image__inner) {
        width: 100%;
        height: 100%;
        object-fit: contain;
        transition: transform 0.3s ease;
        
        &:hover {
          transform: scale(1.05);
        }
      }
      
      .image-loading,
      .image-error {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: var(--el-text-color-secondary);
        
        .loading-icon {
          font-size: 48px;
          margin-bottom: 16px;
          animation: rotate 2s linear infinite;
        }
        
        .el-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }
      }
    }
    
    .product-tags {
      position: absolute;
      top: 16px;
      left: 16px;
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      
      .el-tag {
        font-weight: bold;
        border: none;
      }
    }
  }
  
  .thumbnail-list {
    display: flex;
    gap: 8px;
    margin-top: 16px;
    overflow-x: auto;
    padding: 8px 0;
    
    .thumbnail-item {
      width: 80px;
      height: 80px;
      flex-shrink: 0;
      border: 2px solid transparent;
      border-radius: 6px;
      overflow: hidden;
      cursor: pointer;
      transition: all 0.2s;
      
      &:hover {
        border-color: var(--el-color-primary-light-5);
      }
      
      &.active {
        border-color: var(--el-color-primary);
      }
      
      .thumbnail-image {
        width: 100%;
        height: 100%;
        
        :deep(img) {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }
      
      .thumbnail-error {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        background: var(--el-fill-color-light);
        color: var(--el-text-color-secondary);
      }
    }
  }
}

/* 信息区域 */
.info-section {
  .product-header {
    margin-bottom: 20px;
    
    .product-title {
      font-size: 22px;
      font-weight: 600;
      color: var(--el-text-color-primary);
      margin: 0 0 8px 0;
      line-height: 1.4;
    }
    
    .product-code {
      display: flex;
      align-items: center;
      gap: 8px;
      color: var(--el-text-color-secondary);
      font-size: 14px;
      
      .code-value {
        font-weight: 500;
      }
      
      .copy-btn {
        margin-left: auto;
        padding: 0;
        height: auto;
      }
    }
  }
  
  .price-section {
    margin-bottom: 24px;
    padding: 16px;
    background: linear-gradient(135deg, var(--el-color-primary-light-9) 0%, var(--el-color-primary-light-8) 100%);
    border-radius: 8px;
    
    .price-row {
      display: flex;
      align-items: baseline;
      gap: 12px;
      margin-bottom: 8px;
      
      .current-price {
        display: flex;
        align-items: baseline;
        gap: 2px;
        
        .price-symbol {
          font-size: 18px;
          color: var(--el-color-danger);
          font-weight: 500;
        }
        
        .price-number {
          font-size: 32px;
          color: var(--el-color-danger);
          font-weight: bold;
        }
        
        .discount-percent {
          background: var(--el-color-danger);
          color: white;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: bold;
          margin-left: 8px;
        }
      }
      
      .original-price {
        font-size: 14px;
        color: var(--el-text-color-secondary);
        
        .price-strikethrough {
          text-decoration: line-through;
        }
      }
    }
    
    .price-tips {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      color: var(--el-text-color-secondary);
    }
  }
  
  .rating-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--el-border-color-light);
    
    .rating-content {
      display: flex;
      align-items: center;
      gap: 12px;
      
      .rating-stars {
        :deep(.el-rate__icon) {
          font-size: 18px;
        }
      }
      
      .review-count,
      .sales-count {
        font-size: 14px;
        color: var(--el-text-color-secondary);
      }
    }
  }
  
  .spec-section,
  .quantity-section,
  .shipping-section {
    margin-bottom: 24px;
    
    .section-title {
      font-size: 16px;
      font-weight: 600;
      color: var(--el-text-color-primary);
      margin: 0 0 12px 0;
    }
  }
  
  .spec-section {
    .spec-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
      
      .spec-radio {
        margin: 0;
        
        :deep(.el-radio__label) {
          display: block;
          width: 100%;
        }
        
        .spec-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          
          .spec-name {
            flex: 1;
            font-weight: 500;
          }
          
          .spec-price {
            color: var(--el-color-danger);
            font-weight: bold;
            margin: 0 12px;
          }
          
          .stock-tag {
            font-size: 10px;
            padding: 2px 6px;
          }
          
          .stock-info {
            font-size: 12px;
            color: var(--el-text-color-secondary);
          }
        }
      }
    }
  }
  
  .quantity-section {
    .quantity-control {
      display: flex;
      align-items: center;
      gap: 24px;
      
      .quantity-input {
        width: 120px;
      }
      
      .stock-info {
        font-size: 14px;
        color: var(--el-text-color-secondary);
        
        .out-of-stock-text {
          color: var(--el-color-danger);
        }
      }
    }
  }
  
  .shipping-section {
    .shipping-content {
      display: flex;
      flex-direction: column;
      gap: 12px;
      
      .shipping-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        color: var(--el-text-color-regular);
        
        .el-icon {
          color: var(--el-color-primary);
        }
        
        .address-select {
          width: 180px;
        }
      }
    }
  }
  
  .service-section {
    margin-bottom: 24px;
    padding: 16px;
    background: var(--el-fill-color-light);
    border-radius: 8px;
    
    .service-list {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 16px;
      
      @media (max-width: 480px) {
        grid-template-columns: repeat(2, 1fr);
      }
      
      .service-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        
        .el-icon {
          font-size: 20px;
          color: var(--el-color-primary);
          margin-bottom: 4px;
        }
        
        span {
          font-size: 12px;
          color: var(--el-text-color-secondary);
        }
      }
    }
  }
  
  .action-section {
    .action-buttons {
      display: flex;
      gap: 12px;
      margin-bottom: 16px;
      
      @media (max-width: 480px) {
        flex-direction: column;
      }
      
      .el-button {
        flex: 1;
        height: 48px;
        font-size: 16px;
        font-weight: 500;
        
        .el-icon {
          margin-right: 8px;
        }
      }
      
      .add-cart-btn {
        &:disabled {
          background: var(--el-color-info-light-9);
          border-color: var(--el-color-info-light-8);
          color: var(--el-color-info-light-5);
        }
      }
    }
    
    .quick-actions {
      display: flex;
      justify-content: center;
      gap: 24px;
      
      .el-button {
        color: var(--el-text-color-secondary);
        
        &:hover {
          color: var(--el-color-primary);
        }
        
        .el-icon {
          margin-right: 4px;
        }
      }
    }
  }
}

/* 动画 */
@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 响应式调整 */
@media (max-width: 768px) {
  .quick-view-container {
    max-height: 70vh;
  }
  
  .image-section {
    .main-image-container {
      .main-image {
        height: 300px;
      }
    }
    
    .thumbnail-list {
      .thumbnail-item {
        width: 60px;
        height: 60px;
      }
    }
  }
  
  .info-section {
    .product-header {
      .product-title {
        font-size: 18px;
      }
    }
    
    .price-section {
      .price-row {
        .current-price {
          .price-number {
            font-size: 24px;
          }
        }
      }
    }
    
    .action-section {
      .action-buttons {
        .el-button {
          height: 44px;
          font-size: 14px;
        }
      }
    }
  }
}
</style>